// createRoomShortcut — Phase 6 Task 6.6-B 「ワンボタン作成 UX」共通ロジック.
//
// HomePage のメイン CTA 「ルーム作成」と、`?action=create` で本サイトから
// 着地したケースの両方から呼ばれる共通関数. デフォルト設定 (匿名 / 標準
// canvas / owner password / email なし) で `POST /rooms` を即叩いて normalize
// した outcome を返す.
//
// 設計:
// - 引数は **DispatcherClient の `createRoom` 関数のみ** (test では fake 注入).
//   localStorage への adminToken 保存・URL 操作・spinner 表示は呼び出し側
//   (HomePage / App / RoomSettingsModal) の責務. 本モジュールは「成功した
//   roomId / adminToken」を返すだけのトランスポート薄ラッパ.
// - outcome を 3 種類に絞り込み (ok / rate-limited / error). 詳細設定ページの
//   `messageForFailure` と比べて UX が単純なので 'no-server-available' /
//   'invalid' / 'http-status' は全部 `'error'` の中の `message` 文字列に潰す.
// - `ownerEmail` を渡さないので `emailVerificationSent: false` は本フローでは
//   発生しない. `outcome: 'ok'` の field には含めない (型で表現).
//
// Why a separate module from `createRoomForm.ts`:
// - 詳細フォーム (CreateRoomPage) は per-field validation + 複雑な UI message
//   が要る. ショートカットは「押した瞬間に投げる」だけなので concerns が違う.
// - テストも分けたほうが各関数の責務が明確. createRoomForm は per-field 入力,
//   こちらは「pure transport call」.

import type {
  CreateRoomInput,
  CreateRoomOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';

/**
 * Minimal contract: just the `createRoom` callable. We don't take the entire
 * DispatcherClient so tests can pass any callable without building a fake
 * `lookupRoomServer` too.
 */
export type CreateRoomFn = (input: CreateRoomInput) => Promise<CreateRoomOutcome>;

/**
 * Result of `createRoomShortcut`. 3 outcomes:
 *
 * - `ok`           : ルーム作成成功. roomId / adminToken を呼び出し側に返却.
 * - `rate-limited` : 429. retryAfterMs をそのまま渡す.
 * - `error`        : それ以外の失敗 (network / invalid / http-status /
 *                    no-server-available のどれか). `message` は人が読める
 *                    日本語 1 行 (HomePage の inline error にそのまま出せる).
 */
export type CreateRoomShortcutOutcome =
  | { readonly outcome: 'ok'; readonly roomId: string; readonly adminToken: string }
  | { readonly outcome: 'rate-limited'; readonly retryAfterMs: number; readonly message: string }
  | { readonly outcome: 'error'; readonly message: string };

/**
 * Optional overrides for the shortcut. Currently only canvas size — HomePage
 * exposes a 3-button selector (小 / 標準 / 大) directly under the main CTA so
 * the user can pick a square size without entering the detailed form. Other
 * fields stay on server-default (anonymous, no owner password, no email,
 * default name).
 *
 * 引数なし呼び出し (`?action=create` 経路) は `createRoom({})` を素のまま叩く
 * ので既存挙動と互換. options を渡したときのみ `settings` を組み立てる.
 */
export interface CreateRoomShortcutOptions {
  readonly canvasWidth: number;
  readonly canvasHeight: number;
}

const formatMs = (ms: number): string => {
  const sec = Math.max(1, Math.ceil(ms / 1_000));
  if (sec >= 60 * 60) {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    return minutes > 0 ? `${hours}時間${minutes}分` : `${hours}時間`;
  }
  if (sec >= 60) {
    const mins = Math.floor(sec / 60);
    const remSec = sec % 60;
    return remSec > 0 ? `${mins}分${remSec}秒` : `${mins}分`;
  }
  return `${sec}秒`;
};

/**
 * Default-settings 1-click ルーム作成. 名前なし / settings なし / owner
 * credential なしで `POST /rooms` を叩く. server 側 default で
 * `name='お絵描きルーム'` / `canvas 1500x1500` が割り当たる (デフォルト).
 *
 * 失敗時は人が読める `message` 付きで返す. 呼び出し側は inline error 表示
 * すれば良い.
 */
export const createRoomShortcut = async (
  createRoom: CreateRoomFn,
  options?: CreateRoomShortcutOptions,
): Promise<CreateRoomShortcutOutcome> => {
  // 全て default に倒す. settings は server default に任せる. HomePage の
  // selector で canvas size を選んだケースのみ `options` 経由で settings を
  // 明示する — その場合のみ `settings` を入れて投げる (引数なし呼び出しは
  // `createRoom({})` のまま、既存テストの固定を壊さない).
  const input: CreateRoomInput =
    options === undefined
      ? {}
      : { settings: { canvasWidth: options.canvasWidth, canvasHeight: options.canvasHeight } };
  const out = await createRoom(input);
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', roomId: out.roomId, adminToken: out.adminToken };
    case 'rate-limited':
      return {
        outcome: 'rate-limited',
        retryAfterMs: out.retryAfterMs,
        message: `ルーム作成上限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'no-server-available':
      return {
        outcome: 'error',
        message: 'サーバが満員です。しばらくしてから再度お試しください。',
      };
    case 'error':
      switch (out.cause) {
        case 'network':
          return {
            outcome: 'error',
            message: 'ネットワークエラーです。接続を確認してください。',
          };
        case 'invalid':
          return {
            outcome: 'error',
            message: 'サーバの応答が不正です。時間を置いて再度お試しください。',
          };
        case 'http-status':
          return {
            outcome: 'error',
            message: `サーバエラーが発生しました (HTTP ${out.status ?? '?'})。`,
          };
      }
  }
};
